import app from 'flarum/forum/app';
import { fieldRegistry } from '../common/registry';
import MarkdownEditor from './components/MarkdownEditor';
import ImageUploadInput from './components/ImageUploadInput';
const t = (key, params) => params ? app.translator.trans(`ernestdefoe-page-builder.${key}`, params) : app.translator.trans(`ernestdefoe-page-builder.${key}`);

/* A control-only input component + a value-only display component per field
 * type. The form wrapper supplies the label / help / error; these render just
 * the control or the value. */

function choices(field) {
  return String(field.options?.choices || '').split('\n').map(s => s.trim()).filter(Boolean);
}

// ── Inputs ───────────────────────────────────────────────────────────────
const TextInput = {
  view(v) {
    const a = v.attrs;
    const type = a.field.type === 'email' ? 'email' : a.field.type === 'url' ? 'url' : 'text';
    return m("input", {
      type: type,
      className: "FormControl",
      value: a.value ?? '',
      placeholder: a.field.placeholder || '',
      oninput: e => a.onChange(e.target.value)
    });
  }
};
const TextareaInput = {
  view(v) {
    const a = v.attrs;
    return m("textarea", {
      className: "FormControl",
      rows: a.field.type === 'richtext' ? 8 : 4,
      value: a.value ?? '',
      placeholder: a.field.placeholder || '',
      oninput: e => a.onChange(e.target.value)
    });
  }
};
const NumberInput = {
  view(v) {
    const a = v.attrs;
    return m("input", {
      type: "number",
      className: "FormControl",
      value: a.value ?? '',
      min: a.field.options?.min,
      max: a.field.options?.max,
      oninput: e => a.onChange(e.target.value === '' ? null : Number(e.target.value))
    });
  }
};
const DateInput = {
  view(v) {
    const a = v.attrs;
    const withTime = !!a.field.options?.include_time;
    return m("input", {
      type: withTime ? 'datetime-local' : 'date',
      className: "FormControl",
      value: a.value ?? '',
      oninput: e => a.onChange(e.target.value)
    });
  }
};
const BooleanInput = {
  view(v) {
    const a = v.attrs;
    return m("label", {
      className: "PB-checkLabel"
    }, m("input", {
      type: "checkbox",
      checked: !!a.value,
      onchange: e => a.onChange(e.target.checked)
    }), " ", t('forum.field.yes'));
  }
};
const SelectInput = {
  view(v) {
    const a = v.attrs;
    return m("select", {
      className: "FormControl",
      value: a.value ?? '',
      onchange: e => a.onChange(e.target.value)
    }, m("option", {
      value: ""
    }, "\u2014"), choices(a.field).map(c => m("option", {
      value: c
    }, c)));
  }
};
const MultiselectInput = {
  view(v) {
    const a = v.attrs;
    const selected = Array.isArray(a.value) ? a.value : [];
    return m("div", {
      className: "PB-multiselect"
    }, choices(a.field).map(c => m("label", {
      className: "PB-checkLabel"
    }, m("input", {
      type: "checkbox",
      checked: selected.includes(c),
      onchange: e => a.onChange(e.target.checked ? [...selected, c] : selected.filter(x => x !== c))
    }), ' ', c)));
  }
};
const ColorInput = {
  view(v) {
    const a = v.attrs;
    return m("input", {
      type: "color",
      value: a.value || '#3366ff',
      onchange: e => a.onChange(e.target.value)
    });
  }
};
const RatingInput = {
  view(v) {
    const a = v.attrs;
    const max = Math.max(1, parseInt(a.field.options?.max, 10) || 5);
    const cur = parseInt(a.value, 10) || 0;
    return m("div", {
      className: "PB-ratingInput"
    }, Array.from({
      length: max
    }, (_, i) => i + 1).map(n => m("button", {
      type: "button",
      className: `PB-star ${n <= cur ? 'is-on' : ''}`,
      onclick: () => a.onChange(n)
    }, m("i", {
      className: "fas fa-star"
    }))));
  }
};
const RelationshipInput = {
  view(v) {
    const a = v.attrs;
    const multiple = !!a.field.options?.multiple;
    const value = multiple ? Array.isArray(a.value) ? a.value.join(', ') : '' : a.value ?? '';
    return m("input", {
      type: "text",
      className: "FormControl",
      value: value,
      placeholder: multiple ? t('forum.field.ids_placeholder') : t('forum.field.id_placeholder'),
      oninput: e => {
        const raw = e.target.value;
        a.onChange(multiple ? raw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => n > 0) : parseInt(raw, 10) || null);
      }
    });
  }
};

// ── Displays ─────────────────────────────────────────────────────────────
const TextDisplay = {
  view(v) {
    return m("span", null, v.attrs.value);
  }
};
const UrlDisplay = {
  view(v) {
    const val = String(v.attrs.value || '');
    if (!val) return null;
    const safe = /^https?:\/\//i.test(val);
    return safe ? m("a", {
      href: val,
      target: "_blank",
      rel: "noopener noreferrer"
    }, val) : m("span", null, val);
  }
};
const EmailDisplay = {
  view(v) {
    const val = String(v.attrs.value || '');
    return val ? m("a", {
      href: `mailto:${val}`
    }, val) : null;
  }
};
const TextareaDisplay = {
  view(v) {
    const paras = String(v.attrs.value || '').split(/\n+/).map(s => s.trim()).filter(Boolean);
    return m("div", null, paras.map(p => m("p", null, p)));
  }
};
const RichtextDisplay = {
  view(v) {
    // Sanitized (richtext) or formatter-rendered (markdown) server-side.
    return m("div", {
      className: "PB-richtext"
    }, m.trust(String(v.attrs.value || '')));
  }
};

// `markdown` values are rendered to post-style HTML on the server (mentions,
// emoji, uploaded images, code) — display via Flarum's standard content class.
const MarkdownDisplay = {
  view(v) {
    return m("div", {
      className: "PB-richtext Post-body"
    }, m.trust(String(v.attrs.value || '')));
  }
};
const ImageDisplay = {
  view(v) {
    const val = v.attrs.value;
    const url = typeof val === 'string' ? val : val?.url;
    if (!url) return null;
    return m("span", {
      className: "PB-imageDisplay"
    }, m("img", {
      src: url,
      alt: val && val.alt || '',
      loading: "lazy"
    }));
  }
};
const BooleanDisplay = {
  view(v) {
    return m("span", null, v.attrs.value ? t('forum.field.yes') : t('forum.field.no'));
  }
};
const DateDisplay = {
  view(v) {
    const val = v.attrs.value;
    if (!val) return null;
    try {
      return m("span", null, new Date(val).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }));
    } catch {
      return m("span", null, val);
    }
  }
};
const MultiselectDisplay = {
  view(v) {
    const arr = Array.isArray(v.attrs.value) ? v.attrs.value : [];
    return m("span", null, arr.join(', '));
  }
};
const ColorDisplay = {
  view(v) {
    const val = v.attrs.value;
    if (!val) return null;
    return m("span", {
      className: "PB-colorChip"
    }, m("span", {
      className: "PB-colorSwatch",
      style: {
        background: val
      }
    }), " ", val);
  }
};
const RatingDisplay = {
  view(v) {
    const max = Math.max(1, parseInt(v.attrs.field.options?.max, 10) || 5);
    const cur = parseInt(v.attrs.value, 10) || 0;
    return m("span", {
      className: "PB-ratingDisplay"
    }, Array.from({
      length: max
    }, (_, i) => i + 1).map(n => m("i", {
      className: `fas fa-star ${n <= cur ? 'is-on' : ''}`
    })));
  }
};

/** Register input + display for every core field type. */
export function registerCoreFields() {
  fieldRegistry.register('text', TextInput, TextDisplay);
  fieldRegistry.register('textarea', TextareaInput, TextareaDisplay);
  fieldRegistry.register('richtext', TextareaInput, RichtextDisplay);
  fieldRegistry.register('markdown', MarkdownEditor, MarkdownDisplay);
  fieldRegistry.register('image', ImageUploadInput, ImageDisplay);
  fieldRegistry.register('number', NumberInput, TextDisplay);
  fieldRegistry.register('date', DateInput, DateDisplay);
  fieldRegistry.register('boolean', BooleanInput, BooleanDisplay);
  fieldRegistry.register('select', SelectInput, TextDisplay);
  fieldRegistry.register('multiselect', MultiselectInput, MultiselectDisplay);
  fieldRegistry.register('url', TextInput, UrlDisplay);
  fieldRegistry.register('email', TextInput, EmailDisplay);
  fieldRegistry.register('color', ColorInput, ColorDisplay);
  fieldRegistry.register('rating', RatingInput, RatingDisplay);
  fieldRegistry.register('relationship', RelationshipInput, TextDisplay);
}